// Index of the first queue entry after every contact a burst rang. Contacts no
// longer in the queue are ignored; when none are found, returns `fallback`.
export function nextIndexAfterBurst(
  queueIds: string[],
  rungIds: string[],
  fallback: number
): number {
  const rung = new Set(rungIds)
  let last = -1
  queueIds.forEach((id, i) => {
    if (rung.has(id)) last = i
  })
  return last >= 0 ? last + 1 : fallback
}

// Queue with the given ids removed from position `from` onwards; entries
// before `from` are kept as they are.
export function dropFromTail<T extends { id: string | number }>(
  queue: T[],
  ids: Set<string>,
  from: number
): T[] {
  const next = queue.filter((p, i) => i < from || !ids.has(String(p.id)))
  return next.length === queue.length ? queue : next
}

// Splits the contacts sent for a round into those to step past and those
// never dialled. `sentIds` is in queue order and `legIds` are the contacts the
// service placed a leg for.
// - A contact with a leg is rung.
// - A contact without a leg sent before the last one with a leg was dropped
//   as a duplicate number, so it is stepped past too.
// - A contact without a leg after it is undialled when the governor capped
//   the round (it may have been cut by the cap); uncapped, every contact
//   without a leg was a duplicate and is stepped past.
// - With no legs at all, every sent contact is stepped past.
export function settleRound(
  sentIds: string[],
  legIds: string[],
  capped: boolean
): { rung: string[]; undialled: string[] } {
  const legs = new Set(legIds)
  if (!legs.size || !capped) return { rung: [...sentIds], undialled: [] }
  let lastLeg = -1
  sentIds.forEach((id, i) => {
    if (legs.has(id)) lastLeg = i
  })
  const rung: string[] = []
  const undialled: string[] = []
  sentIds.forEach((id, i) => {
    if (legs.has(id) || i < lastLeg) rung.push(id)
    else undialled.push(id)
  })
  return { rung, undialled }
}

// Splits the contacts sent for a round into those the service kept and those
// it skipped (outside calling hours, duplicate numbers or numbers with no
// country code), both in `sentIds` order. With no burst opened, every sent contact is skipped.
export function splitServiceSkips(
  sentIds: string[],
  res: {
    burst_id: number | null
    outside_hours?: string[] | null
    duplicate_ids?: string[] | null
    invalid_numbers?: string[] | null
  }
): { sent: string[]; skipped: string[] } {
  if (res.burst_id == null) return { sent: [], skipped: [...sentIds] }
  const gone = new Set(
    [
      ...(res.outside_hours ?? []),
      ...(res.duplicate_ids ?? []),
      ...(res.invalid_numbers ?? [])
    ].map(String)
  )
  return {
    sent: sentIds.filter((id) => !gone.has(id)),
    skipped: sentIds.filter((id) => gone.has(id))
  }
}

// The non-winner legs of a burst that have reached a settled status, keyed by
// person id, and whether every non-winner leg has settled.
export function settledLosers<
  S extends string,
  L extends {
    champion_id: string | null
    people_id: number | null
    status: S
  }
>(
  legs: L[],
  winnerId: string,
  settledStatuses: readonly S[]
): { settled: { id: string; status: S }[]; allSettled: boolean } {
  const settled: { id: string; status: S }[] = []
  let allSettled = true
  for (const leg of legs) {
    const id = leg.champion_id ?? leg.people_id
    if (id == null || String(id) === winnerId) continue
    if (settledStatuses.includes(leg.status)) {
      settled.push({ id: String(id), status: leg.status })
    } else {
      allSettled = false
    }
  }
  return { settled, allSettled }
}

// Status of the leg that rang `personId`, or null when no leg did.
export function legStatusFor<
  S extends string,
  L extends {
    champion_id: string | null
    people_id: number | null
    status: S
  }
>(legs: L[], personId: string): S | null {
  const leg = legs.find(
    (l) => String(l.champion_id ?? l.people_id) === personId
  )
  return leg ? leg.status : null
}

// State of a won burst's call: "machine" when the winner leg was a machine,
// "ended" when the burst or the winner leg has ended, else "live".
export function winnerCallState<
  L extends {
    champion_id: string | null
    people_id: number | null
    status: string
    ended_at: string | null
  }
>(
  legs: L[],
  winnerId: string,
  burstEndedAt: string | null
): "machine" | "ended" | "live" {
  const leg = legs.find(
    (l) => String(l.champion_id ?? l.people_id) === winnerId
  )
  if (leg?.status === "machine") return "machine"
  if (burstEndedAt || leg?.ended_at) return "ended"
  return "live"
}

// Queue index of the next person to dial. With a round, the first entry after
// every contact it rang (see nextIndexAfterBurst); without one, index + 1.
export function nextDialIndex(
  queueIds: string[],
  index: number,
  round: { rungIds: string[]; fallback: number } | null
): number {
  if (!round) return index + 1
  return nextIndexAfterBurst(queueIds, round.rungIds, round.fallback)
}

// Whether a machine winner of `burstId` may be rolled past: not when it was
// already handled or while the rep is logging that burst.
export function rollOnAllowed(
  burstId: number,
  handled: ReadonlySet<number>,
  loggingBurstId: number | null
): boolean {
  return !handled.has(burstId) && loggingBurstId !== burstId
}

// Whether a finished log for `loggedBurstId` may still advance past it: only
// while that burst is the open one and it was not rolled past.
export function shouldAdvanceAfterLog(
  loggedBurstId: number | null,
  currentBurstId: number | null,
  rolled: ReadonlySet<number>
): boolean {
  return (
    loggedBurstId != null &&
    loggedBurstId === currentBurstId &&
    !rolled.has(loggedBurstId)
  )
}

// Whether a machine verdict for a winner seen at `wonAt` is still expected.
export function verdictWindowOpen(
  wonAt: number,
  now: number,
  windowMs: number
): boolean {
  return now < wonAt + windowMs
}

// Delay before the next read of a won burst: `fastMs` inside the verdict
// window, `slowMs` after it.
export function winnerWatchDelay(
  wonAt: number,
  now: number,
  windowMs: number,
  fastMs: number,
  slowMs: number
): number {
  return verdictWindowOpen(wonAt, now, windowMs) ? fastMs : slowMs
}

// Whether the leg that rang `winnerId` carries a detection verdict that is not
// one of `machineVerdicts`.
export function personVerdictFor<
  L extends {
    champion_id: string | null
    people_id: number | null
    answered_by?: string | null
  }
>(legs: L[], winnerId: string, machineVerdicts: readonly string[]): boolean {
  const leg = legs.find(
    (l) => String(l.champion_id ?? l.people_id) === winnerId
  )
  const verdict = leg?.answered_by
  return Boolean(verdict) && !machineVerdicts.includes(verdict as string)
}
